// src/components/auth/LoginModal.tsx
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useAuthContext } from '../../services/AuthProvider';
import { useTranslation } from '../../useTranslation';
import { PasswordField } from './PasswordField';
import { AuthCardHeader } from './AuthCardHeader';
import { GoogleSignInButton } from './GoogleSignInButton';

const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToRegister: () => void;
  onForgotPassword?: () => void;
  hideCancel?: boolean;
  inline?: boolean;
  language: string;
  onLanguageChange?: (language: string) => void;
  theme?: 'light' | 'dark';
  onThemeChange?: (theme: 'light' | 'dark') => void;
}

const LoginModal: React.FC<LoginModalProps> = ({
  isOpen,
  onClose,
  onSwitchToRegister,
  onForgotPassword,
  hideCancel,
  inline,
  language,
  onLanguageChange,
  theme,
  onThemeChange,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { login, loginWithGoogle } = useAuthContext();
  const { t } = useTranslation(language);

  if (!isOpen) return null;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      const result = await login(email, password);
      if (!result.success) {
        setError(result.error || 'Login failed');
      } else {
        onClose();
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred during login');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: { credential?: string }) => {
    const credential = credentialResponse.credential;
    if (!credential) {
      setError('Google login failed');
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      const result = await loginWithGoogle(credential);
      if (!result.success) {
        setError(result.error || 'Google login failed');
      } else {
        onClose();
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred during Google login');
    } finally {
      setIsLoading(false);
    }
  };

  const formCard = (
    <div
      className="p-5 sm:p-6 md:p-8 w-full max-w-md mx-auto relative overflow-hidden rounded-2xl border shadow-xl backdrop-blur-xl bg-white/95 dark:bg-slate-800/95 border-stone-200/80 dark:border-slate-600/80 text-stone-900 dark:text-slate-100"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-blue-400 rounded-t-2xl" />

      <AuthCardHeader
        language={language}
        onLanguageChange={onLanguageChange}
        theme={theme}
        onThemeChange={onThemeChange}
      />

      <h2 className="text-xl sm:text-2xl font-semibold text-stone-900 dark:text-white text-center">
        {t.login || 'Welcome Back'}
      </h2>
      <p className="text-stone-600 dark:text-slate-300 text-center mt-1 mb-6 text-sm">
        {t.loginSubtitle}
      </p>

      {error && (
        <div className="bg-red-50/90 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-xl mb-4 text-sm flex items-center gap-2 backdrop-blur">
          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </div>
      )}

      {googleClientId ? (
        <div className="mb-5 space-y-3">
          <GoogleSignInButton
            clientId={googleClientId}
            label={t.loginWithGoogle}
            disabled={isLoading}
            onSuccess={(credential) => void handleGoogleSuccess({ credential })}
            onError={() => setError(t.loginWithGoogle ? `${t.loginWithGoogle} failed` : 'Google login failed')}
          />
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-stone-200 dark:bg-slate-600" />
            <p className="text-xs font-medium uppercase tracking-wide text-stone-400 dark:text-slate-500">
              {t.orContinueWith}
            </p>
            <div className="h-px flex-1 bg-stone-200 dark:bg-slate-600" />
          </div>
        </div>
      ) : null}

      <form onSubmit={handleLogin} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-stone-800 dark:text-slate-200 mb-1">
            {t.email || 'Email'}
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 dark:border-slate-500 bg-white/80 dark:bg-slate-700/50 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-400"
            placeholder="name@example.com"
            required
            autoComplete="email"
          />
        </div>

        <PasswordField
          value={password}
          onChange={setPassword}
          label={t.password || 'Password'}
          required
          autoComplete="current-password"
        />

        {onForgotPassword && (
          <div className="text-right">
            <button
              type="button"
              onClick={onForgotPassword}
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              {t.forgotPassword ?? 'Forgot password?'}
            </button>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-slate-600 dark:hover:bg-slate-500 text-white font-medium py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed transition-colors min-h-[48px]"
        >
          {isLoading ? (
            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : null}
          {t.login || 'Sign In'}
        </button>
      </form>

      <div className="mt-6 text-center space-y-3">
        <p className="text-sm text-stone-700 dark:text-slate-300">
          {t.dontHaveAccount ?? "Don't have an account?"}{' '}
          <button type="button" onClick={onSwitchToRegister} className="font-medium text-blue-600 dark:text-blue-400 hover:underline">
            {t.register ?? 'Create account'}
          </button>
        </p>
        {!hideCancel && (
          <button type="button" onClick={onClose} className="text-sm text-stone-500 hover:text-stone-700 dark:text-slate-400 dark:hover:text-slate-200">
            {t.cancel ?? 'Cancel'}
          </button>
        )}
      </div>
    </div>
  );

  if (inline) {
    return isOpen ? formCard : null;
  }
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-stone-900/40 dark:bg-black/50 backdrop-blur-md flex items-center justify-center z-[9999] p-4 sm:p-6 safe-area-padding">
      {formCard}
    </div>,
    document.body
  );
};

export default LoginModal;
